/**
 * ElektrikTaahhutnameKarti.jsx - the card on /FdsSaha/Istatistikler (backlog #506, backend #505): the stands of the selected fair that
 * need the Elektrik taahhütname, by how far its signatures are - waiting for the decorator, signed by the decorator, signed by the
 * decorator and the stand's electrician. GET /projects/stats?fuarId= -> elektrikTaahhutnamesi {required, awaitingDecorator,
 * decoratorOnly, decoratorAndElectrician}; the backend reads it through the same TaahhutnameDurumu as the badges, so the counts
 * and the list squares never disagree. Colors are the list dots' (seviyeNoktasi); the level texts are the badges' own.
 */
import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { getProjectStats } from "../../services/projectService";
import { seviyeMetni, seviyeNoktasi } from "./taahhutnameYardimci";

const BEKLEYEN_RENGI = "#ced4da";

export default function ElektrikTaahhutnameKarti({ fuarId }) {
  const { t } = useTranslation("saha");
  const [sonuc, setSonuc] = useState(null);   // { fuarId, sayilar } | { fuarId, hata }

  useEffect(() => {
    if (!fuarId) return undefined;
    let iptal = false;
    getProjectStats(fuarId)
      .then((d) => { if (!iptal) setSonuc({ fuarId, sayilar: d?.elektrikTaahhutnamesi ?? null }); })
      .catch((e) => {
        if (!iptal) setSonuc({ fuarId, hata: e?.message || i18n.t("saha:sahaIstatistikler.elektrikTaahhutnamesi.yuklenemedi") });
      });
    return () => { iptal = true; };
  }, [fuarId]);

  const guncel = sonuc && sonuc.fuarId === fuarId ? sonuc : null;
  const s = guncel?.sayilar;
  const parcalar = s ? [
    { alan: "awaitingDecorator", etiket: t("sahaIstatistikler.elektrikTaahhutnamesi.dekoratorBekleniyor"), renk: BEKLEYEN_RENGI },
    { alan: "decoratorOnly", etiket: seviyeMetni("SADECE_DEKORATOR"), renk: seviyeNoktasi("SADECE_DEKORATOR") },
    { alan: "decoratorAndElectrician", etiket: seviyeMetni("DEKORATOR_ELEKTRIKCI"), renk: seviyeNoktasi("DEKORATOR_ELEKTRIKCI") },
  ] : [];

  let govde;
  if (!fuarId) {
    govde = <div className="text-muted small" data-testid="elektrik-karti-fuar">{t("sahaIstatistikler.elektrikTaahhutnamesi.fuarSecin")}</div>;
  } else if (!guncel) {
    govde = <div className="text-center py-2"><Spinner size="sm" color="primary" /></div>;
  } else if (guncel.hata) {
    govde = <div className="text-danger small" data-testid="elektrik-karti-hata">{guncel.hata}</div>;
  } else if (!s) {
    govde = <div className="text-muted small">{t("sahaIstatistikler.elektrikTaahhutnamesi.sayiYok")}</div>;
  } else {
    govde = (
      <>
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="fw-bold fs-4" data-testid="elektrik-karti-gerekli">{s.required ?? 0}</div>
            <div className="text-muted small">{t("sahaIstatistikler.elektrikTaahhutnamesi.gerekli")}</div>
          </Col>
          {parcalar.map((p) => (
            <Col xs={6} md={3} key={p.alan}>
              <div className="d-flex align-items-center gap-2">
                <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: p.renk, flexShrink: 0 }} />
                <span className="fw-bold fs-4" data-testid={`elektrik-karti-${p.alan}`}>{s[p.alan] ?? 0}</span>
              </div>
              <div className="text-muted small">{p.etiket}</div>
            </Col>
          ))}
        </Row>
        {s.required > 0 && (
          <div className="d-flex rounded overflow-hidden mt-3" style={{ height: 10, background: "var(--vz-light, #f3f6f9)" }} aria-hidden="true">
            {parcalar.map((p) => (
              <div key={p.alan} style={{ width: `${((s[p.alan] ?? 0) / s.required) * 100}%`, background: p.renk }} />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <Card className="border-0 shadow-sm mb-4" data-testid="elektrik-taahhutname-karti">
      <CardBody>
        <h6 className="fw-semibold mb-1">{t("sahaIstatistikler.elektrikTaahhutnamesi.baslik")}</h6>
        <p className="text-muted small mb-3">{t("sahaIstatistikler.elektrikTaahhutnamesi.aciklama")}</p>
        {govde}
      </CardBody>
    </Card>
  );
}
